import {
  type Component,
  For,
  Match,
  Show,
  Switch,
  createMemo,
  createResource,
  createSignal,
} from "solid-js";

import { fetchAccess } from "@/api/client";
import type { VmRecord } from "@/api/generated";
import { deleteVm, listVms, startVm, stopVm } from "@/api/vms";

import LaunchWizard from "./LaunchWizard";
import Terminal from "./Terminal";
import { groupVmsForRail, summarizeVmPool, uiStateForVm } from "./computeModel";

type Filter = "all" | "running" | "stopped";

const VmsPage: Component = () => {
  const [vms, { mutate: setVms, refetch }] =
    createResource<VmRecord[]>(listVms);
  const [access] = createResource(fetchAccess);
  const [pending, setPending] = createSignal<string | null>(null);
  const [error, setError] = createSignal<string | null>(null);
  const [attachedVmId, setAttachedVmId] = createSignal<string | null>(null);
  const [filter, setFilter] = createSignal<Filter>("all");

  const records = () => vms() ?? [];
  const railGroups = createMemo(() => groupVmsForRail(records()));
  const summary = createMemo(() => summarizeVmPool(records()));
  const visibleVms = createMemo(() => {
    switch (filter()) {
      case "running":
        return records().filter((vm) => uiStateForVm(vm.state) === "running");
      case "stopped":
        return records().filter((vm) => uiStateForVm(vm.state) === "stopped");
      case "all":
        return records();
    }
  });
  const attachedVm = createMemo(() =>
    records().find((vm) => vm.id === attachedVmId()),
  );

  const onCreated = (rec: VmRecord) => {
    setVms((prev) => (prev ? [rec, ...prev] : [rec]));
  };

  const wrap =
    (op: (id: string) => Promise<VmRecord | undefined>, id: string) =>
    async () => {
      if (pending()) return;
      setPending(id);
      setError(null);
      try {
        const result = await op(id);
        if (result) {
          setVms(
            (prev) => prev?.map((v) => (v.id === id ? result : v)) ?? [result],
          );
        } else {
          setVms((prev) => prev?.filter((v) => v.id !== id));
          if (attachedVmId() === id) setAttachedVmId(null);
        }
      } catch (caught) {
        const apiErr = caught as { message?: string };
        setError(apiErr.message ?? String(caught));
      } finally {
        setPending(null);
      }
    };

  return (
    <div class="neuro-page">
      <div class="neuro-surface">
        <div class="neuro-route-body">
          <h2>Compute</h2>
          <p>
            {summary().total} records · {summary().running} running ·{" "}
            {summary().booting} booting · {summary().failed} failed
          </p>

          <h3>Quota</h3>
          <div class="neuro-stat-grid">
            <Stat
              label="gpu quota"
              value={`${access()?.max_gpus ?? "—"} GB10`}
            />
            <Stat
              label="vm quota"
              value={access() ? `${access()?.max_vms} vm` : "checking"}
            />
            <Stat label="allocated" value={`${summary().allocatedGpus} gpu`} />
          </div>

          <h3>Launch</h3>
          <LaunchWizard onCreated={onCreated} />
          <Show when={error()}>
            {(msg) => (
              <div class="neuro-error-block">
                <span class="neuro-error-title">launch failed</span>
                <span class="neuro-error-detail">{msg()}</span>
              </div>
            )}
          </Show>

          <h3>
            VMs ·{" "}
            <button
              type="button"
              class={`neuro-filter${filter() === "all" ? " is-active" : ""}`}
              onClick={() => setFilter("all")}
            >
              all {summary().total}
            </button>
            <button
              type="button"
              class={`neuro-filter${filter() === "running" ? " is-active" : ""}`}
              onClick={() => setFilter("running")}
            >
              running {summary().running}
            </button>
            <button
              type="button"
              class={`neuro-filter${filter() === "stopped" ? " is-active" : ""}`}
              onClick={() => setFilter("stopped")}
            >
              stopped {summary().stopped}
            </button>
            <button
              type="button"
              class="neuro-action subtle"
              onClick={() => {
                void refetch();
              }}
            >
              refresh
            </button>
          </h3>

          <Show when={!vms.loading} fallback={<p>loading VMs…</p>}>
            <Show when={vms.error}>
              <div class="neuro-error-block">
                <span class="neuro-error-title">failed to load VMs</span>
                <span class="neuro-error-detail">
                  {(vms.error as { message?: string })?.message ??
                    String(vms.error)}
                </span>
              </div>
            </Show>
            <Show
              when={visibleVms().length > 0}
              fallback={<p>no VMs match — launch one above.</p>}
            >
              <table class="neuro-table">
                <thead>
                  <tr>
                    <th>name</th>
                    <th>status</th>
                    <th>size</th>
                    <th>image</th>
                    <th>updated</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  <For each={visibleVms()}>
                    {(vm) => (
                      <tr>
                        <td>
                          <b>{vm.spec.name}</b>
                          <br />
                          <span
                            class="num"
                            style="color: var(--text-tertiary); font-size: 11px;"
                          >
                            {vm.id.slice(0, 12)}
                          </span>
                        </td>
                        <td class="muted">{uiStateForVm(vm.state)}</td>
                        <td class="num muted">
                          {vm.spec.gpu_count > 0
                            ? `gpu·${vm.spec.gpu_count}`
                            : `cpu·${vm.spec.cpu}`}{" "}
                          · {vm.spec.memory_gib} GiB
                        </td>
                        <td class="num muted">{vm.spec.image}</td>
                        <td class="num muted">{formatTime(vm.updated_at)}</td>
                        <td>
                          <RowActions
                            vm={vm}
                            pending={pending() === vm.id}
                            attached={attachedVmId() === vm.id}
                            onStart={wrap(startVm, vm.id)}
                            onStop={wrap(stopVm, vm.id)}
                            onDelete={wrap(
                              (id) => deleteVm(id).then(() => undefined),
                              vm.id,
                            )}
                            onTerminal={() =>
                              setAttachedVmId(
                                attachedVmId() === vm.id ? null : vm.id,
                              )
                            }
                          />
                        </td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            </Show>
          </Show>

          <Show when={attachedVm()}>
            {(vm) => (
              <>
                <h3>terminal · {vm().spec.name}</h3>
                <Terminal
                  vmId={vm().id}
                  onClose={() => setAttachedVmId(null)}
                />
              </>
            )}
          </Show>

          <h3>Pool · by spec</h3>
          <div class="neuro-stat-grid">
            <For each={railGroups()}>
              {(group) => (
                <Stat
                  label={group.label}
                  value={String(group.count)}
                  meta={group.disabled ? "not implemented" : undefined}
                />
              )}
            </For>
          </div>
        </div>
      </div>
    </div>
  );
};

const Stat: Component<{ label: string; value: string; meta?: string }> = (
  props,
) => (
  <div class="neuro-stat">
    <span class="neuro-stat-label">{props.label}</span>
    <span class="neuro-stat-value">{props.value}</span>
    <Show when={props.meta}>
      <span class="neuro-stat-meta">{props.meta}</span>
    </Show>
  </div>
);

const RowActions: Component<{
  vm: VmRecord;
  pending: boolean;
  attached: boolean;
  onStart: () => Promise<void>;
  onStop: () => Promise<void>;
  onDelete: () => Promise<void>;
  onTerminal: () => void;
}> = (props) => (
  <div style="display: flex; gap: 8px; flex-wrap: wrap;">
    <Switch>
      <Match when={props.vm.state === "running"}>
        <button type="button" class="neuro-action" onClick={props.onTerminal}>
          {props.attached ? "detach" : "terminal"}
        </button>
        <button
          type="button"
          class="neuro-action"
          disabled={props.pending}
          onClick={props.onStop}
        >
          stop
        </button>
      </Match>
      <Match
        when={
          props.vm.state === "stopped" ||
          props.vm.state === "failed" ||
          props.vm.state === "pending"
        }
      >
        <button
          type="button"
          class="neuro-action"
          disabled={props.pending}
          onClick={props.onStart}
        >
          start
        </button>
      </Match>
    </Switch>
    <button
      type="button"
      class="neuro-action subtle"
      disabled={props.pending}
      onClick={props.onDelete}
    >
      delete
    </button>
  </div>
);

function formatTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default VmsPage;
